import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { FEATURE_CATALOG, Feature } from '../../shared/features-catalog';

interface Testimonial { initials: string; name: string; role: string; quote: string; }
interface Stat { display: string; label: string; count?: number; suffix?: string; }

/**
 * Feature-Übersicht / Konfigurator (/produkt/features).
 *
 * Portiert aus der gelieferten Standalone-App (ClickUp 869ecurh9). Header/Footer
 * kommen aus dem App-Layout (DefaultLayoutComponent). Das Anfrage-Formular nutzt
 * ausschließlich einen mailto:-Fallback an lukas@marktbox.de (kein Backend).
 */
@Component({
  selector: 'app-features-overview-page',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './features-overview-page.component.html',
  styleUrls: ['./features-overview-page.component.scss'],
})
export class FeaturesOverviewPageComponent implements AfterViewInit {
  private router = inject(Router);
  private host: ElementRef<HTMLElement> = inject(ElementRef);
  private title = inject(Title);
  private meta = inject(Meta);

  constructor() {
    // SEO (SSR): Ziel-Keyword „Verkaufsautomat".
    this.title.setTitle($localize`Verkaufsautomat-Features: 11 smarte Funktionen – marktbox`);
    this.meta.updateTag({
      name: 'description',
      content: $localize`Alle Funktionen der marktbox auf einen Blick: Presets, Rabatte, Reinigungsprotokoll, Reservierungen & mehr – so verkaufen Sie mit dem Verkaufsautomaten autonom und sicher.`,
    });
  }

  /** Geteilte Feature-Liste (Single Source of Truth) – siehe features-catalog.ts. */
  readonly features: Feature[] = FEATURE_CATALOG;

  readonly testimonials: Testimonial[] = [
    { initials: 'SR', name: 'Sven Röttgen', role: 'Hof Röttgen', quote: $localize`Die Features schaffen die nötige Prozesssicherheit, um unsere Automaten sauber auszusteuern – unvergleichbar mit unseren Nicht-marktbox-Automaten.` },
    { initials: 'S', name: 'Saskia', role: 'Joldelunder Bäcker', quote: $localize`Die anfängliche Fehlerquote ist komplett beseitigt. Presets und Reinigungsprotokoll geben uns echte Prozesssicherheit – und dem Team Sicherheit.` },
    { initials: 'SS', name: 'Steffen Stadtler', role: 'Henningsen Backwaren', quote: $localize`Mit Presets, Rabatten, Reinigung & Reservierungen haben wir über 11 Automaten rund 23 % mehr Absatz erreicht – unser Team feiert die marktbox.` },
  ];

  readonly stats: Stat[] = [
    { display: '11', count: 11, label: $localize`Features für deinen Verkauf` },
    { display: '24/7', label: $localize`autonom verkaufen` },
    { display: $localize`1 App`, label: $localize`alles zentral steuern` },
    { display: '0 €', count: 0, suffix: ' €', label: $localize`keine neue Hardware` },
  ];

  // --- Warenkorb / Auswahl --------------------------------------------------
  readonly selected = signal<string[]>([]);
  readonly selectedCount = computed(() => this.selected().length);
  readonly selectedFeatures = computed(() => this.features.filter((f) => this.selected().includes(f.slug)));

  /** Zeigt die fixe Auswahl-Leiste unten und schafft dafür Platz (Padding). */
  @HostBinding('class.has-selection') get hasSelection(): boolean {
    return this.selectedCount() > 0;
  }

  isSel(slug: string): boolean { return this.selected().includes(slug); }
  toggle(slug: string): void {
    const s = this.selected();
    this.selected.set(s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]);
  }
  remove(slug: string): void { this.selected.set(this.selected().filter((x) => x !== slug)); }

  /**
   * „Hinzufügen"/„Hinzugefügt"-Button: Die Auswahl passiert ausschließlich
   * hierüber – ein Klick auf die Karte selbst öffnet dagegen die Detailseite.
   * stopPropagation verhindert, dass der Karten-Klick (Navigation) mitfeuert.
   */
  addToggle(e: Event, slug: string): void { e.stopPropagation(); this.toggle(slug); }

  /**
   * Klick auf Karte bzw. „Mehr erfahren" → interne Detailseite des Features.
   * Features ohne eigene Detailseite (hasPage:false) erscheinen laut Katalog nur
   * in der Übersicht und navigieren NICHT (sonst Wildcard-Redirect zur Startseite).
   */
  open(e: Event, feature: Feature): void {
    e.stopPropagation();
    if (!feature.hasPage) return;
    this.router.navigateByUrl(feature.url);
  }

  /**
   * Sanftes Scrollen zur Feature-Übersicht bzw. zur Anfrage – statt einer
   * Router-Fragment-Navigation (die die Auswahl zurücksetzen würde).
   */
  scrollTo(e: Event, id: string): void {
    e.preventDefault();
    this.host.nativeElement.querySelector('#' + id)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // --- Anfrage-Formular (mailto-only) --------------------------------------
  name = '';
  email = '';
  message = '';
  readonly sending = signal(false);
  readonly sent = signal(false);

  submit(): void {
    const items = this.selectedFeatures();
    if (!items.length || !this.email.trim()) return;
    this.mailto(items);
    this.sent.set(true);
  }

  /** Anfrage über das E-Mail-Programm des Nutzers an lukas@marktbox.de. */
  private mailto(items: Feature[]): void {
    const body = [
      $localize`Hallo marktbox-Team,`, '', $localize`ich möchte folgende Features anfragen:`, '',
      ...items.map((f) => '- ' + f.title), '',
      $localize`Name: ` + this.name, $localize`E-Mail: ` + this.email,
      ...(this.message ? ['', $localize`Nachricht:`, this.message] : []),
      '', $localize`Viele Grüße`,
    ];
    window.location.href =
      'mailto:lukas@marktbox.de?subject=' +
      encodeURIComponent($localize`marktbox – Feature-Anfrage (` + items.length + ')') +
      '&body=' + encodeURIComponent(body.join('\n'));
  }

  // --- Effekte (Scroll-Reveal, zählende Kennzahlen) ------------------------
  ngAfterViewInit(): void {
    // „js"-Klasse auf den Host (nicht <html>) – passend zur View-Encapsulation.
    this.host.nativeElement.classList.add('js');
    const reduce = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Scroll-Reveal
    const reveals = Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>('.reveal'));
    if (reduce || typeof IntersectionObserver === 'undefined') {
      reveals.forEach((r) => r.classList.add('reveal-in'));
    } else {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('reveal-in'); io.unobserve(e.target); } });
      }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
      reveals.forEach((r) => io.observe(r));
    }

    // Zählende Kennzahlen
    const band = this.host.nativeElement.querySelector('.band');
    if (band) {
      const stats = () => this.host.nativeElement.querySelectorAll<HTMLElement>('.stat .n[data-count]');
      // SSR / kein rAF: Endwerte direkt setzen, ohne Animation. requestAnimationFrame
      // existiert beim Server-Prerender nicht – sonst ReferenceError in ngAfterViewInit.
      if (typeof requestAnimationFrame === 'undefined' || typeof IntersectionObserver === 'undefined') {
        stats().forEach((el) => {
          el.textContent = (parseFloat(el.getAttribute('data-count') || '0') || 0) + (el.getAttribute('data-suffix') || '');
        });
      } else {
        const run = () => {
          stats().forEach((el) => {
            const target = parseFloat(el.getAttribute('data-count') || '0') || 0;
            const suf = el.getAttribute('data-suffix') || '';
            const dur = 1400; let start = 0;
            const step = (ts: number) => {
              if (!start) start = ts;
              const p = Math.min((ts - start) / dur, 1);
              el.textContent = Math.round(target * (1 - Math.pow(1 - p, 3))) + suf;
              if (p < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
          });
        };
        const io2 = new IntersectionObserver((entries) => {
          entries.forEach((e) => { if (e.isIntersecting) { run(); io2.disconnect(); } });
        }, { threshold: 0.4 });
        io2.observe(band);
      }
    }
  }
}
